import type {
    VerticalScanConfig,
    VerticalScanUpdateSource,
    WinchStatusSnapshot,
    WinchTelemetry,
} from '../winchMqttApi';

export type CageWinchMqttState = {
    online: boolean;
    telemetry: WinchTelemetry | null;
    config: WinchStatusSnapshot | null;
    verticalScan: VerticalScanConfig | null;
    verticalScanSource: VerticalScanUpdateSource | null;
};

export type CageWinchMqttAction =
    | { type: 'RESET' }
    | { type: 'SET_ONLINE'; payload: boolean }
    | { type: 'SET_TELEMETRY'; payload: WinchTelemetry }
    | { type: 'SET_CONFIG'; payload: WinchStatusSnapshot; replacePositions?: boolean }
    | {
          type: 'SET_VERTICAL_SCAN';
          payload: VerticalScanConfig;
          source: VerticalScanUpdateSource;
      };

export const initialCageWinchMqttState: CageWinchMqttState = {
    online: false,
    telemetry: null,
    config: null,
    verticalScan: null,
    verticalScanSource: null,
};

function coordsEqual(
    left: { distance: number; depth: number } | null | undefined,
    right: { distance: number; depth: number } | null | undefined
): boolean {
    if (left == null && right == null) {
        return true;
    }
    if (left == null || right == null) {
        return false;
    }
    return left.distance === right.distance && left.depth === right.depth;
}

export function telemetryEquals(current: WinchTelemetry | null, next: WinchTelemetry): boolean {
    if (current == null) {
        return false;
    }
    return (
        current.state === next.state &&
        current.distance === next.distance &&
        current.depth === next.depth &&
        current.targetDistance === next.targetDistance &&
        current.targetDepth === next.targetDepth &&
        current.calibrated === next.calibrated &&
        current.allowSmartMovement === next.allowSmartMovement
    );
}

export function statusEquals(
    current: WinchStatusSnapshot | null,
    next: WinchStatusSnapshot
): boolean {
    if (current == null) {
        return false;
    }
    const currentLimits = current.limits;
    const nextLimits = next.limits;
    const limitsMatch =
        currentLimits == null && nextLimits == null
            ? true
            : currentLimits != null &&
              nextLimits != null &&
              currentLimits.distance.min === nextLimits.distance.min &&
              currentLimits.distance.max === nextLimits.distance.max &&
              currentLimits.depth.min === nextLimits.depth.min &&
              currentLimits.depth.max === nextLimits.depth.max;

    return (
        limitsMatch &&
        current.speed === next.speed &&
        coordsEqual(current.servicePosition, next.servicePosition) &&
        coordsEqual(current.measurementPosition, next.measurementPosition)
    );
}

export function verticalScanEquals(
    current: VerticalScanConfig | null,
    next: VerticalScanConfig
): boolean {
    if (current == null) {
        return false;
    }
    return (
        current.enabled === next.enabled &&
        current.x === next.x &&
        current.yMin === next.yMin &&
        current.yMax === next.yMax
    );
}

function mergeVerticalScan(
    current: VerticalScanConfig | null,
    next: VerticalScanConfig,
    source: VerticalScanUpdateSource
): VerticalScanConfig {
    if (source === 'setlimits' && current != null) {
        return { ...next, enabled: current.enabled };
    }
    return next;
}

export function cageWinchMqttReducer(
    state: CageWinchMqttState,
    action: CageWinchMqttAction
): CageWinchMqttState {
    switch (action.type) {
        case 'RESET':
            return initialCageWinchMqttState;
        case 'SET_ONLINE':
            if (state.online === action.payload) {
                return state;
            }
            return { ...state, online: action.payload };
        case 'SET_TELEMETRY':
            if (telemetryEquals(state.telemetry, action.payload)) {
                return state;
            }
            return { ...state, telemetry: action.payload };
        case 'SET_CONFIG': {
            const current = state.config;
            const incoming = action.payload;
            const replacePositions = action.replacePositions === true;
            const payload: WinchStatusSnapshot = {
                limits: incoming.limits ?? current?.limits ?? null,
                speed: incoming.speed ?? current?.speed ?? null,
                savedPositions: replacePositions
                    ? incoming.savedPositions
                    : incoming.savedPositions.length > 0
                      ? incoming.savedPositions
                      : (current?.savedPositions ?? []),
                measurementPosition: replacePositions
                    ? (incoming.measurementPosition ?? null)
                    : (incoming.measurementPosition ?? current?.measurementPosition ?? null),
                servicePosition: replacePositions
                    ? (incoming.servicePosition ?? null)
                    : (incoming.servicePosition ?? current?.servicePosition ?? null),
            };
            if (statusEquals(state.config, payload)) {
                return state;
            }
            return { ...state, config: payload };
        }
        case 'SET_VERTICAL_SCAN': {
            const payload = mergeVerticalScan(state.verticalScan, action.payload, action.source);
            if (
                verticalScanEquals(state.verticalScan, payload) &&
                state.verticalScanSource === action.source
            ) {
                return state;
            }
            return { ...state, verticalScan: payload, verticalScanSource: action.source };
        }
        default:
            return state;
    }
}
